import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Table,
  Spinner,
  Input,
} from 'reactstrap'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FaTruck,
  FaUser,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaSearch,
  FaFilter,
  FaEye,
  FaArrowRight
} from 'react-icons/fa'
import { fetchMyRequests } from '../../store/slices/transportRequestSlice'
import './TransportRequest.scss'

const AllNominations = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { requests = [], loading } = useSelector(state => state.transportRequest)

  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    dispatch(fetchMyRequests({ page: 1, limit: 100 }))
  }, [dispatch])

  // Filter requests that have nominations
  const nominations = requests.filter(req => req.truck_nomination)

  const filteredNominations = nominations.filter(nom => {
    const matchesSearch =
      searchQuery === '' ||
      nom.request_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      nom.origin?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      nom.destination?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesSearch
  })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="management-page"
    >
      <Container fluid>
        <div className="page-header">
          <motion.div variants={itemVariants}>
            <h2 className="page-title neo-text">All Nominations</h2>
            <p className="page-subtitle">Track and manage all vehicle and driver assignments</p>
          </motion.div>
        </div>

        <Row className="mb-4 g-3">
          <Col md={6}>
            <div className="search-box position-relative">
              <FaSearch className="position-absolute translate-middle-y top-50 ms-3 text-muted" />
              <Input
                type="text"
                placeholder="Search by request ID or location..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="glass-input ps-5"
              />
            </div>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3 text-muted">Loading nomination list...</p>
          </div>
        ) : (
          <motion.div variants={itemVariants}>
            {filteredNominations.length === 0 ? (
              <div className="empty-state-card text-center py-5 glass-effect rounded-4">
                <FaTruck size={48} className="text-muted opacity-20 mb-3" />
                <h4 className="neo-text">No Nominations Found</h4>
                <p className="text-muted">Requests with assigned vehicles will appear here.</p>
              </div>
            ) : (
              <div className="comparison-table">
                <Table hover responsive className="mb-0 border-0">
                  <thead>
                    <tr>
                      <th>Request ID</th>
                      <th>Route</th>
                      <th>Loading Date</th>
                      <th>Status</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {filteredNominations.map(req => (
                        <motion.tr
                          key={req.request_id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <td><span className="fw-bold text-primary">{req.request_number}</span></td>
                          <td>
                            <small className="text-muted d-block">
                              {req.origin} <FaArrowRight size={10} className="mx-1" /> {req.destination}
                            </small>
                          </td>
                          <td>{new Date(req.loading_date).toLocaleDateString()}</td>
                          <td><Badge color="success" className="status-badge">Nominated</Badge></td>
                          <td className="text-center">
                            <Button
                              className="btn-gradient p-2 d-inline-flex"
                              size="sm"
                              onClick={() => navigate(`/transport-request/${req.request_id}/nomination`)}
                            >
                              <FaEye />
                            </Button>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </Table>
              </div>
            )}
          </motion.div>
        )}
      </Container>
    </motion.div>
  )
}

export default AllNominations
